/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {BulkActionEntity} from '@ion/core/src/types/entities';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {BulkUploadContainer} from '@ion/dls/src/imports/BulkUploadContainer';
import useEntityLabel from '../hooks/useEntityLabel';
import {BulkSelectEntitiesFormFields, isValidFile} from './BulkSelectEntitiesEditorUtils';
import useBulkSelectedEntitiesImport from './useBulkSelectedEntitiesImport';

export interface BulkFileUploadProps {
  isImportDisabled: boolean;
  bulkUploadContainerId: string;
  entity: BulkActionEntity;
  isBulkAction: boolean;
  formState: FormState;
  setIsBinDisabled: (enabled: boolean) => void;
  setIsImportDisabled: (enabled: boolean) => void;
  setFormState?: (newState: FormState) => void;
}

const useStyles = makeStyles(() => ({
  uploadContainer : {
    flex : 1
  },
  or : {
    display        : 'flex',
    fontSize       : '16px',
    textTransform  : 'uppercase',
    alignItems     : 'center',
    justifyContent : 'center',
    flex           : 'none',
    fontWeight     : 500,
    padding        : '0 30px'
  }
}));

/**
 * Displays File Pond for Bulk File Uploads & contains all related functionality.
 */
export const BulkFileUpload = ({
  isImportDisabled,
  bulkUploadContainerId,
  entity,
  isBulkAction,
  formState,
  setFormState,
  setIsBinDisabled,
  setIsImportDisabled
}: BulkFileUploadProps) => {
  const classes                               = useStyles();
  const existingImportStatus                  = formState[BulkSelectEntitiesFormFields.File]?.value?.importStatus;
  const [importStatus, setImportStatus]       = useState<boolean>(existingImportStatus);
  const [isEmptyFile, setIsEmptyFile]         = useState<boolean>(false);
  const [isInvalidFormat, setIsInvalidFormat] = useState<boolean>(false);

  const {
    singularEntityLabel,
    pluralEntityLabel
  } = useEntityLabel(entity);

  const {
    dialogBulkAction
  } = translate('common:editors.bulkActions', {returnObjects: true, entity: singularEntityLabel});

  const {
    templateColumns,
    importParams,
    importResults,
    handleImport
  } = useBulkSelectedEntitiesImport({
    entity,
    setImportStatus,
    setIsEmptyFile,
    setIsInvalidFormat,
    isBulkAction
  });

  useEffect(function(): void {
    setImportStatus(existingImportStatus);
  }, [existingImportStatus]);

  useEffect(function(): void {
    const isComplete = importParams?.isComplete && !importParams?.isFailure;
    if (isComplete) {
      setIsBinDisabled(true);
      setIsImportDisabled(true);
      onImportSuccess?.(importParams, false);
    }
  }, [importParams]);

  const onClearImport = useCallback(function(): void {
    onImportSuccess?.(importParams, true);
    setIsBinDisabled(false);
    setIsImportDisabled(false);
    setImportStatus(false);

    // in order to support drag and drop (with same file), we have to clear the value of the dropsheet container
    const importContainer = document.getElementById(bulkUploadContainerId) as HTMLInputElement;
    if (importContainer) {
      importContainer.value = null;
    }
  }, [importParams]);

  const onImportSuccess = function(file, shouldClear = false): void {
    const updatedImportParams = {
      importParams : {
        items      : [],
        isComplete : false,
        isFailure  : false,
        workbook   : null
      },
      importComplete : false,
      importStatus   : false
    };

    if (shouldClear) {
      setFormState({
        [BulkSelectEntitiesFormFields.File] : {
          name     : BulkSelectEntitiesFormFields.File,
          value    : updatedImportParams,
          error    : false,
          required : false
        },
        [BulkSelectEntitiesFormFields.EntityIdentifiers] : {
          name     : BulkSelectEntitiesFormFields.EntityIdentifiers,
          required : true
        }
      });
    } else {
      const previouslyImportedFile = formState[BulkSelectEntitiesFormFields.File]?.value?.importParams;
      if (!isValidFile(previouslyImportedFile)) {
        if (isValidFile(file)) {
          updatedImportParams.importParams = file;
        }
      } else {
        updatedImportParams.importParams = previouslyImportedFile;
      }

      if (isValidFile(updatedImportParams.importParams)) {
        updatedImportParams.importComplete = true;
        updatedImportParams.importStatus = true;
        setFormState({
          [BulkSelectEntitiesFormFields.File] : {
            name     : BulkSelectEntitiesFormFields.File,
            value    : updatedImportParams,
            required : true
          },
          [BulkSelectEntitiesFormFields.EntityIdentifiers] : {
            name     : BulkSelectEntitiesFormFields.EntityIdentifiers,
            value    : [],
            error    : false,
            required : false
          }
        });
      }
    }
  };

  return (
    <>
      <div className={classes.uploadContainer}>
        <BulkUploadContainer ignoreEntity
          isFormField
          disabled={isImportDisabled}
          id={bulkUploadContainerId}
          uploadTitle={dialogBulkAction.uploadTitle}
          entity={entity}
          entityLabel={pluralEntityLabel}
          omitInstructions
          omitDownloadEntityTemplate
          templateColumns={templateColumns}
          templateOptions={{}}
          onDownload={null}
          isEmptyFile={isEmptyFile}
          isInvalidFormat={isInvalidFormat}
          isBulkAction={isBulkAction}
          handleImport={handleImport}
          importParams={formState[BulkSelectEntitiesFormFields.File]?.value?.importParams}
          isRequired={formState[BulkSelectEntitiesFormFields.File]?.required}
          importComplete={!!formState[BulkSelectEntitiesFormFields.File]?.value?.importComplete}
          importStatus={importStatus}
          clearImport={onClearImport} />
      </div>
      <div className={classes.or}>
        {translate('common:conjunctions.or')}
      </div>
    </>
  );
};

export default BulkFileUpload;
